import { LitElement, html } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators.js';
import { modalController } from 'lxl-utils/lxl-portal';
import styles from './lxl-component-modal.scss';

@customElement('lxl-component-modal')
export class LxlComponentModal extends LitElement {
  static styles = styles;

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    mode: 'open' as ShadowRootMode,
  };

  @property({ type: Boolean }) 'prevent-backdrop-close';
  @property({ type: String }) heading = '';
  @property({ type: String }) description = '';

  @query('.lxl-component-modal') modalContent;
  @query('.lxl-component-modal-close') closeButton;

  private previousActiveElement: HTMLElement | null = null;
  private titleId = `modal-title-${Math.random().toString(36).slice(2)}`;
  private descId = `modal-desc-${Math.random().toString(36).slice(2)}`;

  firstUpdated() {
    this.previousActiveElement = document.activeElement as HTMLElement;

    setTimeout(() => {
      this.modalContent?.focus();
    }, 0);

    this.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleKeyDown.bind(this));
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
    this.focusOnTabs();
    super.disconnectedCallback();
  }

  private backdropClose() {
    if (!this['prevent-backdrop-close']) {
      this.close();
    }
  }

  close() {
    document.body.removeAttribute('style');
    modalController.removeAll();
    this.focusOnTabs();
  }

  focusOnTabs() {
    const myLoyaltyComponent = document.querySelector('lxl-app-my-loyalty');
    if (myLoyaltyComponent && myLoyaltyComponent.shadowRoot) {
      const tabsComponent = myLoyaltyComponent.shadowRoot.querySelector('lxl-component-tabs');
      if (tabsComponent && tabsComponent.shadowRoot) {
        const firstTab = tabsComponent.shadowRoot.querySelector('.lxl-component-tabs__item');
        if (firstTab instanceof HTMLElement) {
          firstTab.focus();
        }
      }
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.body.style.overflowY = 'hidden';
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.manageFocus(event);
    } else if (event.key === 'Enter' && event.target === this.closeButton) {
      this.close();
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  private manageFocus(event: KeyboardEvent) {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    const isTabPressed = event.key === 'Tab';

    if (!this.shadowRoot?.activeElement) {
      firstFocusableElement.focus();
      event.preventDefault();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      firstFocusableElement.focus();
      event.preventDefault();
    } else if (event.shiftKey && document.activeElement === firstFocusableElement) {
      lastFocusableElement.focus();
      event.preventDefault();
    }
  }

  private getFocusableElements(): HTMLElement[] {
    if (!this.shadowRoot) return [];

    const selector = ['button', '[href]', 'input', 'select', 'textarea', '[tabindex]:not([tabindex="-1"])'].join(',');

    return Array.from(this.shadowRoot.querySelectorAll(selector)).filter((el: Element) => {
      const element = el as HTMLElement;
      return !element.hasAttribute('disabled') && !element.hasAttribute('hidden') && element.offsetWidth > 0 && element.offsetHeight > 0;
    }) as HTMLElement[];
  }

  render() {
    return html`
      <div class="lxl-component-modal-backdrop" @click=${this.backdropClose}>
        <div
          class="lxl-component-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="${this.titleId}"
          aria-describedby="${this.descId}"
          tabindex="-1"
          @keydown="${this.handleKeyDown}"
        >
          <button class="lxl-component-modal-close" @click=${this.close} aria-label="Close dialog">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                id="Vector"
                d="M19.75 1.41667L18.5833 0.25L10 8.83333L1.41667 0.25L0.25 1.41667L8.83333 10L0.25 18.5833L1.41667 19.75L10 11.1667L18.5833 19.75L19.75 18.5833L11.1667 10L19.75 1.41667Z"
                fill="black"
              />
            </svg>
          </button>
          <div class="lxl-component-modal-content">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lxl-component-modal': LxlComponentModal;
  }
}
